// communityDetail/container.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Import API functions
import { 
  getCommunity, 
  updateCommunityInfo, 
  updateCommunityImage,
  getAvailableTags,
  validateCommunityMembership,
  getCommunityTariffs,
  getVenuesGroup,
  inviteCommunityMembers,
} from 'api';

// Import view
import CommunityDetailView from './view';

// Import context
import { useProfile } from 'context';

const CommunityDetailContainer = () => {
  const { t } = useTranslation();
  const { slug } = useParams();
  const navigate = useNavigate();
  const { profile } = useProfile();

  // Main data state
  const [community, setCommunity] = useState(null);
  const [members, setMembers] = useState([]);
  const [venues, setVenues] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [admissionQuestions, setAdmissionQuestions] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [userRole, setUserRole] = useState(null); // User's role in this community

  // UI state
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  
  // Notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Filter state for members
  const [memberFilters, setMemberFilters] = useState({
    search: '',
    status: 'all',
    role: 'all',
  });

  // Fetch all community data
  const fetchCommunityData = useCallback(async () => {
    try {
      setLoading(true);
      
      const response = await getCommunity(slug);
      
      if (response) {
        setCommunity(response.group);
        setMembers(response.members || []);
        setVenues(response.venues || []);
        setUpcomingEvents(response.upcoming_events || []);
        setAdmissionQuestions(response.admission_questions || []);
        
        // Store user's role - if staff, give full admin access
        // Staff users accessing via /manage/community/:slug should have full access
        const isStaff = profile?.staff?.is_staff;
        const communityRole = response.group?.user_role || response.my_membership?.role || null;
        setUserRole(isStaff ? 'AD' : communityRole); // Staff get Admin role
        
        // Initialize form data
        setFormData({
          name: response.group?.name || '',
          slug: response.group?.slug || '',
          description: response.group?.description || '',
          promotional_text: response.group?.promotional_text || '',
          rules: response.group?.rules || '',
          contact_email: response.group?.contact_email || '',
          space_limit: response.group?.space_limit || 0,
          status: response.group?.status || 'PP',
          default_status: response.group?.default_status || 'PP',
          default_role: response.group?.default_role || 'PA',
          visibility: response.group?.visibility || 'PU',
          requires_admission_questions: response.group?.requires_admission_questions || false,
          use_fees: response.group?.use_fees || false,
          data_protection: response.group?.data_protection || 'NO',
          event_tags: response.group?.event_tags || [],
          image: response.group?.image || null,
          background: response.group?.background || null,
          admission_questions: response.admission_questions || [],
        });
      }

      // Fetch available tags
      try {
        const tagsResponse = await getAvailableTags();
        setAvailableTags(Array.isArray(tagsResponse?.data || tagsResponse) ? (tagsResponse?.data || tagsResponse) : []);
      } catch (err) {
        console.warn('Could not fetch tags:', err);
      }

    } catch (error) {
      console.error('Error fetching community:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load community data',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [slug, profile?.staff?.is_staff]);

  useEffect(() => {
    if (slug) {
      fetchCommunityData();
    }
  }, [slug, fetchCommunityData]);

  // Form handlers
  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    setHasChanges(true);
  };

  // Save community changes
  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Prepare data for API
      const updateData = {
        ...formData,
        slug: community.slug,
      };

      await updateCommunityInfo(slug, updateData);
      
      setSnackbar({
        open: true,
        message: 'Community updated successfully',
        severity: 'success',
      });
      setHasChanges(false);
      
      // Refresh data
      fetchCommunityData();
      
    } catch (error) {
      console.error('Error saving community:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to save changes',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle image upload
  const handleImageUpload = async (type) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      // Validate file size
      const maxSize = type === 'image' ? 5 * 1024 * 1024 : 10 * 1024 * 1024;
      if (file.size > maxSize) {
        setSnackbar({
          open: true,
          message: `File too large. Max ${type === 'image' ? '5MB' : '10MB'}`,
          severity: 'error',
        });
        return;
      }
      
      try {
        setSaving(true);
        const formDataUpload = new FormData();
        formDataUpload.append(type, file);
        
        await updateCommunityImage(slug, formDataUpload);
        
        setSnackbar({
          open: true,
          message: 'Image uploaded successfully',
          severity: 'success',
        });
        
        fetchCommunityData();
      } catch (error) {
        console.error('Error uploading image:', error);
        setSnackbar({
          open: true,
          message: 'Failed to upload image',
          severity: 'error',
        });
      } finally {
        setSaving(false);
      }
    };
    
    input.click();
  };

  // Member management
  const handleMemberAction = async (memberSlug, action, data = {}) => {
    try {
      setSaving(true);
      
      await validateCommunityMembership({
        membership_slug: memberSlug,
        action: action,
        ...data,
      });
      
      setSnackbar({
        open: true,
        message: `Member ${action} successfully`,
        severity: 'success',
      });
      
      // Refresh member list
      fetchCommunityData();
      
    } catch (error) {
      console.error('Error updating member:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to update member',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  // Invite member to community
  const handleInviteMember = async (inviteData) => {
    try {
      setSaving(true);
      
      await inviteCommunityMembers(slug, {
        emails: [inviteData.email],
        role: inviteData.role,
        personal_message: inviteData.personal_message,
      });
      
      setSnackbar({
        open: true,
        message: `Invitation sent to ${inviteData.email}`,
        severity: 'success',
      });
      
    } catch (error) {
      console.error('Error inviting member:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to send invitation',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  // Filter members
  const filteredMembers = members.filter(member => {
    // Search filter
    if (memberFilters.search) {
      const searchLower = memberFilters.search.toLowerCase();
      const name = `${member.user?.first_name || ''} ${member.user?.last_name || ''}`.toLowerCase();
      const email = (member.user?.email || '').toLowerCase();
      if (!name.includes(searchLower) && !email.includes(searchLower)) {
        return false;
      }
    }
    
    // Status filter
    if (memberFilters.status !== 'all' && member.status !== memberFilters.status) {
      return false;
    }
    
    // Role filter
    if (memberFilters.role !== 'all' && member.role !== memberFilters.role) {
      return false;
    }
    
    return true;
  });

  // Stats calculations
  const stats = {
    totalMembers: members.length,
    activeMembers: members.filter(m => m.status === 'AC').length,
    pendingMembers: members.filter(m => m.status === 'PP').length,
    totalVenues: venues.length,
    upcomingEvents: upcomingEvents.length,
    admissionQuestions: admissionQuestions.length,
  };

  // Tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Cancel changes
  const handleCancel = () => {
    if (hasChanges) {
      // Reset form to original data
      fetchCommunityData();
      setHasChanges(false);
    }
  };

  // Navigate back - go to community view page
  const handleBack = () => {
    navigate(`/community/${slug}`);
  };

  // Tag management
  const handleTagAdd = (tag) => {
    if (!tag || formData.event_tags?.some(t => t.id === tag.id)) return;
    
    handleFormChange('event_tags', [...(formData.event_tags || []), tag]);
  };

  const handleTagRemove = (tagId) => {
    handleFormChange(
      'event_tags',
      (formData.event_tags || []).filter(t => t.id !== tagId)
    );
  };

  // Question management
  const handleQuestionAdd = (question) => {
    const newQuestion = {
      ...question,
      id: `new_${Date.now()}`,
      is_new: true,
      order: (formData.admission_questions || []).length,
    };
    handleFormChange('admission_questions', [...(formData.admission_questions || []), newQuestion]);
  };

  const handleQuestionUpdate = (questionId, updatedQuestion) => {
    handleFormChange(
      'admission_questions',
      (formData.admission_questions || []).map(q => 
        (q.id === questionId) ? { ...q, ...updatedQuestion } : q
      )
    );
  };

  const handleQuestionDelete = (questionId) => {
    handleFormChange(
      'admission_questions',
      (formData.admission_questions || []).map(q => 
        (q.id === questionId) ? { ...q, is_active: false } : q
      )
    );
  };

  const handleQuestionReorder = (reorderedQuestions) => {
    handleFormChange('admission_questions', reorderedQuestions);
  };

  return (
    <CommunityDetailView
      // Data
      community={community}
      members={filteredMembers}
      allMembers={members}
      venues={venues}
      upcomingEvents={upcomingEvents}
      admissionQuestions={formData.admission_questions || []}
      availableTags={availableTags}
      tariffs={tariffs}
      formData={formData}
      stats={stats}
      userRole={userRole}
      
      // State
      loading={loading}
      saving={saving}
      activeTab={activeTab}
      hasChanges={hasChanges}
      snackbar={snackbar}
      memberFilters={memberFilters}
      
      // Handlers
      onTabChange={handleTabChange}
      onFormChange={handleFormChange}
      onSave={handleSave}
      onCancel={handleCancel}
      onBack={handleBack}
      onImageUpload={handleImageUpload}
      onMemberAction={handleMemberAction}
      onMemberFilterChange={setMemberFilters}
      onInviteMember={handleInviteMember}
      onCloseSnackbar={handleCloseSnackbar}
      onTagAdd={handleTagAdd}
      onTagRemove={handleTagRemove}
      onQuestionAdd={handleQuestionAdd}
      onQuestionUpdate={handleQuestionUpdate}
      onQuestionDelete={handleQuestionDelete}
      onQuestionReorder={handleQuestionReorder}
    />
  );
};

export default CommunityDetailContainer;
